import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { AuthProvider } from './auth/AuthProvider';
import { RequireRole } from './auth/RequireRole';
import { Layout } from './components/Layout';
import { FullPageSpinner } from './components/ui/Spinner';
import { ToastProvider } from './components/ui/Toast';
import { DashboardPage } from './pages/DashboardPage';
import { LoginPage } from './pages/LoginPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { RegisterPage } from './pages/RegisterPage';

const SeriesListPage = lazy(() => import('./pages/SeriesListPage'));
const SeriesNewPage = lazy(() => import('./pages/series-new/SeriesNewPage'));
const SeriesDetailPage = lazy(() => import('./pages/series-detail/SeriesDetailPage'));
const VetoPage = lazy(() => import('./pages/veto/VetoPage'));
const PlayersPage = lazy(() => import('./pages/PlayersPage'));
const PlayerProfilePage = lazy(() => import('./pages/player/PlayerProfilePage'));
const StatsPage = lazy(() => import('./pages/StatsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const AdminPage = lazy(() => import('./pages/admin/AdminPage'));

const page = (node: ReactNode) => <Suspense fallback={<FullPageSpinner />}>{node}</Suspense>;

const router = createBrowserRouter([
  {
    element: (
      <ToastProvider>
        <AuthProvider>
          <Layout />
        </AuthProvider>
      </ToastProvider>
    ),
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'series', element: page(<SeriesListPage />) },
      {
        path: 'series/new',
        element: page(
          <RequireRole role="organizer">
            <SeriesNewPage />
          </RequireRole>,
        ),
      },
      { path: 'series/:id', element: page(<SeriesDetailPage />) },
      { path: 'series/:id/veto', element: page(<VetoPage />) },
      { path: 'players', element: page(<PlayersPage />) },
      { path: 'players/:id', element: page(<PlayerProfilePage />) },
      { path: 'stats', element: page(<StatsPage />) },
      { path: 'login', element: <LoginPage /> },
      { path: 'register', element: <RegisterPage /> },
      {
        path: 'profile',
        element: page(
          <RequireRole role="player">
            <ProfilePage />
          </RequireRole>,
        ),
      },
      {
        path: 'admin',
        element: page(
          <RequireRole role="admin">
            <AdminPage />
          </RequireRole>,
        ),
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);

export function App() {
  return <RouterProvider router={router} />;
}
